import FetchClient from "./FetchClient";
import type { VariantCriteria } from "../types/Product";

export interface Lead {
  id: string; // UPC
  sku: string;
  category: string;
  adjustedCategory?: string;
  lowestPrice: number;
  msrp: number;
  onlinePrice: number;
  image: string;
  title: string;
  brand: string;
  lastUpdated: number;
  source: string;
  variantCriteria?: VariantCriteria[];
}

export interface LeadsResponse {
  categories?: string[];
  manualCategories?: string[];
  hiddenCategories?: string[];
  categoryLabels?: Record<string, string>;
  data: Lead[];
  count: number;
}

export interface AddLeadRequest {
  upc: string;
  sku: string;
  category: string;
  lowestPrice: number;
  msrp: number;
  onlinePrice: number;
  image: string;
  title: string;
  brand: string;
  /** Leads table to write to ("walmart", "home_depot", "target", ...). */
  retailer: string;
}

export interface AddLeadResponse {
  success: boolean;
  message: string;
  lead: Lead;
}

export const getLeads = async (retailer?: string): Promise<LeadsResponse> => {
  const endpoint = retailer
    ? `/leads?retailer=${encodeURIComponent(retailer)}`
    : "/leads";
  const response = await FetchClient.get<LeadsResponse>(endpoint);
  return response;
};

export const deleteLeadCategory = async (
  retailer: string,
  category: string,
  categoryType: "manual" | "master" = "manual"
): Promise<{ success: boolean; message: string }> => {
  const query = new URLSearchParams({ retailer, category, type: categoryType });
  return FetchClient.delete<{ success: boolean; message: string }>(
    `/admin/lead-categories?${query.toString()}`
  );
};

export const reorderLeadCategories = async (
  retailer: string,
  categories: string[]
): Promise<{ success: boolean; message: string; categories: string[] }> =>
  FetchClient.put<{ success: boolean; message: string; categories: string[] }>(
    "/admin/lead-categories/order",
    { retailer, categories }
  );

export const updateLeadCategoryLabel = async (
  retailer: string,
  category: string,
  categoryType: "manual" | "master",
  label: string
): Promise<{ success: boolean; message: string; categoryKey: string; label: string }> =>
  FetchClient.put<{ success: boolean; message: string; categoryKey: string; label: string }>(
    "/admin/lead-categories/label",
    { retailer, category, type: categoryType, label }
  );

export const addLead = async (
  leadData: AddLeadRequest
): Promise<AddLeadResponse> => {
  const response = await FetchClient.post<AddLeadResponse>("/leads", leadData);
  return response;
};

/** Category labels configured on a retailer's Discord lead channels (app_config). */
export const getDiscordLeadCategories = async (retailer: string): Promise<string[]> => {
  const response = await FetchClient.get<{ data: { categories: string[] }; status: number }>(
    `/config/discord-lead-categories/${encodeURIComponent(retailer || "walmart")}`
  );
  return response.data?.categories ?? [];
};

/** Category labels configured on Discord Walmart lead channels (app_config). */
export const getWalmartDiscordLeadCategories = (): Promise<string[]> => getDiscordLeadCategories("walmart");

export interface UpdateLeadRequest {
  upc?: string;
  sku?: string;
  category?: string;
  lowestPrice?: number;
  msrp?: number;
  onlinePrice?: number;
  image?: string;
  title?: string;
}

export interface UpdateLeadResponse {
  success: boolean;
  message: string;
  lead: Lead;
}

export interface FindLeadOptionsResponse extends UpdateLeadResponse {
  options: VariantCriteria[];
}

export const updateLead = async (leadId: string, data: UpdateLeadRequest, retailer?: string): Promise<UpdateLeadResponse> => {
  const query = retailer ? `?retailer=${encodeURIComponent(retailer)}` : "";
  return FetchClient.patch<UpdateLeadResponse>(`/admin/leads/${encodeURIComponent(leadId)}${query}`, { body: data });
};

export const findLeadOptions = async (leadId: string, retailer?: string): Promise<FindLeadOptionsResponse> => {
  const query = retailer ? `?retailer=${encodeURIComponent(retailer)}` : "";
  return FetchClient.post<FindLeadOptionsResponse>(`/admin/leads/${encodeURIComponent(leadId)}/find-options${query}`, {});
};

export const deleteLead = async (leadId: string, retailer?: string): Promise<{ success: boolean; message: string }> => {
  const query = retailer ? `?retailer=${encodeURIComponent(retailer)}` : "";
  return FetchClient.delete<{ success: boolean; message: string }>(`/admin/leads/${encodeURIComponent(leadId)}${query}`);
};
